import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';


export default function FounderPage() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const floatVariants = {
    initial: { y: 0, rotateZ: -5 },
    animate: {
      y: [-20, 20, -20],
      rotateZ: [-5, 5, -5],
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800;14..32,900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
          scroll-padding-top: 100px;
        }

        body {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: linear-gradient(135deg, #f5f7ff 0%, #ffffff 50%, #f0f4ff 100%);
          color: #0a0c1a;
          overflow-x: hidden;
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        :root {
          --primary: #2563eb;
          --primary-dark: #1d4ed8;
          --primary-light: #3b82f6;
          --secondary: #4f46e5;
          --accent: #06b6d4;
          --dark: #0a0c1a;
          --gray: #1e293b;
          --light: #f8fafc;
          --glass: rgba(255, 255, 255, 0.72);
          --glass-border: rgba(37, 99, 235, 0.18);
          --shadow-sm: 0 4px 20px rgba(0, 0, 0, 0.02);
          --shadow-md: 0 20px 35px -12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 35px 60px -15px rgba(37, 99, 235, 0.2);
          --shadow-xl: 0 50px 80px -20px rgba(37, 99, 235, 0.3);
          --transition-butter: cubic-bezier(0.16, 1, 0.3, 1);
          --transition-smooth: cubic-bezier(0.2, 0.9, 0.4, 1.1);
          --transition-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* ========== ANIMATED BACKGROUND ========== */
        .founder-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -2;
          background: linear-gradient(135deg, #eef2ff 0%, #ffffff 30%, #f8faff 60%, #f5f7ff 100%);
          overflow: hidden;
        }

        .founder-bg::before {
          content: '';
          position: absolute;
          width: 100vw;
          height: 100vh;
          background: radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.08), transparent 50%);
          animation: orbitSlow1 25s infinite alternate ease-in-out;
          border-radius: 50%;
          filter: blur(120px);
        }

        .founder-bg::after {
          content: '';
          position: absolute;
          width: 90vw;
          height: 90vh;
          background: radial-gradient(circle, rgba(79, 70, 229, 0.06), transparent 60%);
          bottom: -10%;
          right: -10%;
          animation: orbitSlow2 30s infinite alternate ease-in-out;
          border-radius: 50%;
          filter: blur(100px);
        }

        @keyframes orbitSlow1 {
          0% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          100% { transform: translate(15%, 20%) scale(1.4); opacity: 0.8; }
        }

        @keyframes orbitSlow2 {
          0% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          100% { transform: translate(-15%, -15%) scale(1.3); opacity: 0.7; }
        }

        /* ========== FLOATING PARTICLES ========== */
        .particles {
          position: fixed;
          inset: 0;
          z-index: -1;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 2px;
          height: 2px;
          background: var(--primary);
          border-radius: 50%;
          opacity: 0;
          animation: particleFloat 8s infinite ease-in;
        }

        @keyframes particleFloat {
          0% {
            transform: translateY(100vh) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) translateX(100px);
            opacity: 0;
          }
        }

        /* ========== CURSOR GLOW ========== */
        .cursor-glow {
          position: fixed;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(37, 99, 235, 0.15) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          z-index: 999;
          filter: blur(40px);
          will-change: transform;
        }

        /* ========== HERO SECTION ========== */
        .founder-hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 120px 24px;
          position: relative;
          overflow: hidden;
        }

        .founder-hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          max-width: 1400px;
          width: 100%;
          z-index: 10;
        }

        .founder-text-section h1 {
          font-size: clamp(48px, 8vw, 84px);
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -0.03em;
          background: linear-gradient(
            135deg,
            #0a0c1a 0%,
            var(--primary) 35%,
            var(--secondary) 70%,
            var(--accent) 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 32px;
          word-spacing: -0.05em;
        }

        .founder-subtitle {
          font-size: clamp(16px, 2.5vw, 22px);
          font-weight: 600;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 24px;
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .badge-dot {
          width: 12px;
          height: 12px;
          background: var(--primary);
          border-radius: 50%;
          animation: badgePulse 2s infinite;
        }

        @keyframes badgePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 var(--primary); }
          50% { transform: scale(1.1); box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
        }

        .founder-description {
          font-size: clamp(15px, 1.8vw, 18px);
          color: #475569;
          line-height: 1.8;
          margin-bottom: 32px;
          max-width: 550px;
        }

        .certs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 16px;
          margin-bottom: 40px;
        }

        .cert-badge {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border: 1.5px solid rgba(37, 99, 235, 0.2);
          padding: 16px;
          border-radius: 16px;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          color: var(--dark);
          transition: all 0.4s var(--transition-smooth);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .cert-badge::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent, rgba(37, 99, 235, 0.1), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s;
        }

        .cert-badge:hover::before {
          transform: translateX(100%);
        }

        .cert-badge:hover {
          transform: translateY(-8px);
          border-color: var(--primary);
          background: rgba(37, 99, 235, 0.05);
          box-shadow: 0 20px 40px rgba(37, 99, 235, 0.2);
        }

        .founder-cta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn-founder-primary {
          background: linear-gradient(105deg, #0a0c1a, var(--primary), var(--secondary));
          color: white;
          padding: 18px 48px;
          border-radius: 60px;
          font-weight: 700;
          font-size: 16px;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.4s var(--transition-smooth);
          box-shadow: 0 20px 40px rgba(37, 99, 235, 0.4);
          cursor: pointer;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }

        .btn-founder-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.7s ease;
        }

        .btn-founder-primary:hover::before {
          left: 100%;
        }

        .btn-founder-primary:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 30px 60px rgba(37, 99, 235, 0.5);
        }

        .btn-founder-secondary {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(16px);
          border: 2px solid var(--primary);
          padding: 16px 48px;
          border-radius: 60px;
          text-decoration: none;
          color: var(--primary);
          font-weight: 700;
          font-size: 16px;
          transition: all 0.4s var(--transition-smooth);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }

        .btn-founder-secondary:hover {
          background: var(--primary);
          color: white;
          transform: translateY(-8px);
          box-shadow: 0 25px 50px rgba(37, 99, 235, 0.35);
        }

        .founder-image-wrapper {
          position: relative;
          height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .founder-card {
          position: relative;
          width: 100%;
          max-width: 400px;
          height: 500px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 40px;
          border: 1.5px solid rgba(37, 99, 235, 0.25);
          padding: 40px;
          box-shadow: 0 50px 100px rgba(37, 99, 235, 0.25);
          transition: all 0.5s var(--transition-smooth);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        .founder-card:hover {
          transform: scale(1.05) rotateY(2deg);
          border-color: var(--primary);
          box-shadow: 0 60px 120px rgba(37, 99, 235, 0.35);
        }

        .founder-avatar {
          width: 180px;
          height: 180px;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 80px;
          font-weight: 900;
          color: white;
          margin-bottom: 24px;
          box-shadow: 0 20px 50px rgba(37, 99, 235, 0.3);
          animation: avatarPulse 3s infinite;
        }

        @keyframes avatarPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .founder-card h2 {
          font-size: 28px;
          font-weight: 800;
          color: var(--dark);
          margin-bottom: 12px;
        }

        .founder-role {
          font-size: 14px;
          color: var(--primary);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }

        .founder-card-desc {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .social-links {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-top: auto;
        }

        .social-icon {
          width: 44px;
          height: 44px;
          background: rgba(37, 99, 235, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          text-decoration: none;
          font-size: 18px;
          transition: all 0.3s;
          border: 1px solid rgba(37, 99, 235, 0.2);
        }

        .social-icon:hover {
          background: var(--primary);
          color: white;
          transform: translateY(-6px) scale(1.1);
          border-color: var(--primary);
        }

        /* ========== STATS SECTION ========== */
        .founder-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
          margin-top: 80px;
          padding: 60px 24px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border: 1.5px solid rgba(37, 99, 235, 0.15);
          padding: 32px;
          border-radius: 32px;
          text-align: center;
          transition: all 0.4s var(--transition-smooth);
          cursor: pointer;
        }

        .stat-card:hover {
          transform: translateY(-12px);
          border-color: var(--primary);
          background: rgba(37, 99, 235, 0.05);
          box-shadow: 0 30px 60px rgba(37, 99, 235, 0.2);
        }

        .stat-number {
          font-size: 48px;
          font-weight: 900;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* ========== EXPERTISE SECTION ========== */
        .founder-expertise {
          padding: 100px 24px;
          position: relative;
          z-index: 10;
        }

        .expertise-title {
          text-align: center;
          font-size: clamp(32px, 5vw, 56px);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin-bottom: 60px;
          background: linear-gradient(135deg, #0a0c1a, var(--primary));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .expertise-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .expertise-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 32px;
          padding: 40px;
          border: 1.5px solid rgba(37, 99, 235, 0.15);
          transition: all 0.5s var(--transition-smooth);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .expertise-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(120deg, transparent, rgba(37, 99, 235, 0.08), transparent);
          transition: left 0.7s;
        }

        .expertise-card:hover::before {
          left: 100%;
        }

        .expertise-card:hover {
          transform: translateY(-16px) scale(1.02);
          border-color: var(--primary);
          background: white;
          box-shadow: 0 40px 80px rgba(37, 99, 235, 0.2);
        }

        .expertise-icon {
          font-size: 48px;
          margin-bottom: 20px;
          display: inline-block;
        }

        .expertise-card h3 {
          font-size: 24px;
          font-weight: 800;
          color: var(--dark);
          margin-bottom: 14px;
        }

        .expertise-card p {
          color: #64748b;
          line-height: 1.7;
          font-size: 15px;
        }

        /* ========== ACHIEVEMENTS ========== */
        .founder-achievements {
          padding: 100px 24px;
          position: relative;
          z-index: 10;
        }

        .achievements-title {
          text-align: center;
          font-size: clamp(32px, 5vw, 56px);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin-bottom: 60px;
          background: linear-gradient(135deg, var(--secondary), var(--primary));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .achievements-list {
          max-width: 900px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
        }

        .achievement-item {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-radius: 28px;
          padding: 32px;
          border: 1.5px solid rgba(37, 99, 235, 0.15);
          transition: all 0.4s var(--transition-smooth);
          position: relative;
          overflow: hidden;
        }

        .achievement-item::after {
          content: '';
          position: absolute;
          top: -100%;
          right: -100%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(37, 99, 235, 0.1), transparent);
          border-radius: 50%;
          transition: all 0.6s;
        }

        .achievement-item:hover::after {
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
        }

        .achievement-item:hover {
          transform: translateY(-10px);
          border-color: var(--primary);
          box-shadow: 0 30px 60px rgba(37, 99, 235, 0.15);
        }

        .achievement-check {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          margin-bottom: 16px;
          font-size: 18px;
        }

        .achievement-item h4 {
          font-size: 18px;
          font-weight: 700;
          color: var(--dark);
          margin-bottom: 10px;
        }

        .achievement-item p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
        }

        /* ========== RESPONSIVE DESIGN ========== */
        @media (max-width: 1024px) {
          .founder-hero-content {
            grid-template-columns: 1fr;
            gap: 40px;
            padding: 40px 0;
          }

          .founder-image-wrapper {
            height: 400px;
          }

          .founder-card {
            height: auto;
            max-width: 100%;
          }

          .founder-text-section h1 {
            font-size: 52px;
          }
        }

        @media (max-width: 768px) {
          .founder-hero {
            min-height: auto;
            padding: 80px 16px;
          }

          .founder-hero-content {
            grid-template-columns: 1fr;
          }

          .founder-text-section h1 {
            font-size: 40px;
          }

          .founder-subtitle {
            flex-direction: column;
            align-items: flex-start;
          }

          .certs-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .btn-founder-primary,
          .btn-founder-secondary {
            padding: 14px 32px;
            font-size: 14px;
            width: 100%;
          }

          .expertise-grid,
          .achievements-list {
            grid-template-columns: 1fr;
          }

          .founder-card {
            padding: 30px;
            height: auto;
          }

          .founder-avatar {
            width: 140px;
            height: 140px;
            font-size: 60px;
          }

          .founder-stats {
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            padding: 40px 16px;
          }
        }

        @media (max-width: 480px) {
          .founder-hero {
            padding: 60px 12px;
          }

          .founder-text-section h1 {
            font-size: 32px;
          }

          .founder-description {
            font-size: 14px;
          }

          .certs-grid {
            grid-template-columns: 1fr;
          }

          .cert-badge {
            width: 100%;
          }

          .founder-cta {
            flex-direction: column;
          }

          .btn-founder-primary,
          .btn-founder-secondary {
            width: 100%;
          }

          .founder-card h2 {
            font-size: 22px;
          }

          .stat-number {
            font-size: 36px;
          }

          .stat-card {
            padding: 24px;
          }

          .expertise-card,
          .achievement-item {
            padding: 28px 20px;
          }

          .expertise-card h3 {
            font-size: 20px;
          }
        }

        /* ========== ANIMATIONS ========== */
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ========== SCROLLBAR ========== */
        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, var(--primary), var(--secondary));
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--primary-dark);
        }
      `}</style>

      <div className="founder-bg"></div>

      <div
        className="cursor-glow"
        style={{
          transform: `translate(${mousePosition.x - 100}px, ${mousePosition.y - 100}px)`,
        }}
      />

      {/* Particles */}
      <div className="particles">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 5 + 's',
              animationDuration: (Math.random() * 4 + 6) + 's',
            }}
          />
        ))}
      </div>

      {/* HERO SECTION */}
      <motion.section
        className="founder-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="founder-hero-content">
          {/* LEFT - TEXT CONTENT */}
          <motion.div
            className="founder-text-section"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1 variants={itemVariants}>
              Lucky Ali Memon
            </motion.h1>

            <motion.div className="founder-subtitle" variants={itemVariants}>
              <span className="badge-dot"></span>
              Founder & CTO
            </motion.div>

            <motion.p className="founder-description" variants={itemVariants}>
              6x Salesforce Certified architect with 3+ years building enterprise-grade CRM systems, high-converting Shopify stores, and scalable React applications.
            </motion.p>

            <motion.p className="founder-description" variants={itemVariants}>
              I build reliable digital solutions from the ground up—from robust CRM automation pipelines to intuitive, fast-loading React interfaces designed for real-world business needs.
            </motion.p>

            <motion.div className="certs-grid" variants={itemVariants}>
              <div className="cert-badge">🏆 Salesforce Agentforce Ai</div>
              <div className="cert-badge">🚀 Platform Developer II</div>
              <div className="cert-badge">⚡ React Developer</div>
              <div className="cert-badge">💳 Shopify Developer</div>
              <div className="cert-badge">🌐 Salesforce Certified</div>
              <div className="cert-badge">📊 Data Architect</div>
            </motion.div>

           <motion.div className="founder-cta" variants={itemVariants}>

  <a
    href="mailto:luckymemon2234@gmail.com"
    className="btn-founder-primary"
  >
    Email →luckymemon2234@gmail.com
  </a>

  <a
    href="https://luckyali.web.app/"
    target="_blank"
    rel="noopener noreferrer"
    className="btn-founder-secondary"
  >
    View Portfolio ↗
  </a>

</motion.div>
          </motion.div>

          {/* RIGHT - CARD */}
          <motion.div
            className="founder-image-wrapper"
            variants={floatVariants}
            initial="initial"
            animate="animate"
          >
            <motion.div
              className="founder-card"
              whileHover={{ rotateY: 10, rotateX: -5 }}
              transition={{ duration: 0.6 }}
            >
              <div className="founder-avatar">LA</div>
              <h2>Lucky Ali Memon</h2>
              <p className="founder-role">Founder & CTO</p>
              <p className="founder-card-desc">
                Building high-performance digital systems that drive real business growth
              </p>
              <div className="social-links">
                <a href="#" className="social-icon" title="LinkedIn">
                  in
                </a>
                <a href="#" className="social-icon" title="Twitter">
                  𝕏
                </a>
                <a href="#" className="social-icon" title="GitHub">
                  ⚙
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* STATS SECTION */}
      <motion.div
        className="founder-stats"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
        >
          <div className="stat-number">3+</div>
          <div className="stat-label"> 3+ years of production experience</div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
        >
          <div className="stat-number">50+</div>
          <div className="stat-label">Successful Projects</div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
        >
          <div className="stat-number">95%</div>
          <div className="stat-label">Client Satisfaction</div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
        >
          <div className="stat-number">150+</div>
          <div className="stat-label">shopify and react themes</div>
        </motion.div>
      </motion.div>

      {/* EXPERTISE SECTION */}
      <motion.section
        className="founder-expertise"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2 className="expertise-title">Core Expertise</h2>

        <motion.div
          className="expertise-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">⚙️</div>
            <h3>Salesforce Architecture</h3>
            <p>Enterprise CRM systems, custom configurations, Apex development, and large-scale integrations</p>
          </motion.div>

          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">🛍️</div>
            <h3>Shopify Mastery</h3>
            <p>High-converting storefronts, custom themes, app development, and revenue optimization</p>
          </motion.div>

          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">⚛️</div>
            <h3>React & Frontend</h3>
            <p>Complex applications, performance optimization, and world-class user experiences</p>
          </motion.div>

          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">🔌</div>
            <h3>API & Integration</h3>
            <p>Seamless system integrations, RESTful APIs, webhooks, and data synchronization</p>
          </motion.div>

          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">☁️</div>
            <h3>Cloud Infrastructure</h3>
            <p>AWS, serverless architecture, DevOps, and production-grade deployment pipelines</p>
          </motion.div>

          <motion.div className="expertise-card" variants={itemVariants}>
            <div className="expertise-icon">🤖</div>
            <h3>AI & Automation</h3>
            <p>AI tools, workflow automation, process optimization, and intelligent systems</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* ACHIEVEMENTS SECTION */}
      <motion.section
        className="founder-achievements"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2 className="achievements-title">What Sets Me Apart</h2>

        <motion.div
          className="achievements-list"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>Production-Proven Systems</h4>
            <p>Built with reliable architecture, rigorous testing, and resilient uptime for seamless daily operations</p>
          </motion.div>

          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>Revenue-Focused Architecture</h4>
            <p>Systems designed to increase revenue, reduce costs, and maximize growth</p>
          </motion.div>

          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>Enterprise-Grade Quality</h4>
            <p>Code that meets Fortune 500 standards with security, scalability, and reliability</p>
          </motion.div>

          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>Fast Delivery & Scaling</h4>
            <p>Rapid MVP development to full-scale systems without technical debt</p>
          </motion.div>

          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>Mentorship & Leadership</h4>
            <p>Building high-performing teams and guiding technical strategy for growth</p>
          </motion.div>

          <motion.div className="achievement-item" variants={itemVariants}>
            <div className="achievement-check">✓</div>
            <h4>24/7 Support Ready</h4>
            <p>Always available for critical systems and strategic technical guidance</p>
          </motion.div>
        </motion.div>
      </motion.section>
    </>
  );
}